import React from "react";
import {
  BadgeCheck,
  Bus,
  Car,
  CheckCircle2,
  Globe2,
  Handshake,
  Headphones,
  HeartHandshake,
  Hotel,
  Luggage,
  MapPin,
  Plane,
  ShieldCheck,
  Users,
} from "lucide-react";
import "./About.css";
import aboutImage from "../../assets/hero-top/about.png";
import aboutIcon from "../../assets/hero-top/about-icon.png";
import usePageMeta from "../../hooks/usePageMeta";

const promises = [
  {
    icon: <ShieldCheck size={18} />,
    title: "Convenient Travel",
    text: "Travel services brought together in one platform.",
  },
  {
    icon: <Users size={18} />,
    title: "Customer Focused",
    text: "Assistance for enquiries, bookings and travel requirements.",
  },
  {
    icon: <Globe2 size={18} />,
    title: "Domestic & International",
    text: "Travel options across India and international destinations.",
  },
];

const offers = [
  {
    icon: <Plane size={18} />,
    label: "Flight Booking",
    text: "Domestic and international flight options with convenient booking assistance.",
  },
  {
    icon: <Hotel size={18} />,
    label: "Hotel Booking",
    text: "Accommodation options across popular domestic and international destinations.",
  },
  {
    icon: <MapPin size={18} />,
    label: "Holiday Packages",
    text: "Ready-made and customized holiday packages for different types of travellers.",
  },
  {
    icon: <Car size={18} />,
    label: "Cab & Transfers",
    text: "Airport transfers, intercity transportation and destination-based cab services.",
  },
  {
    icon: <Bus size={18} />,
    label: "Bus Booking",
    text: "Bus travel options for selected routes and destinations.",
  },
  {
    icon: <BadgeCheck size={18} />,
    label: "Visa Assistance",
    text: "Visa assistance and documentation support for selected international destinations.",
  },
];

const reasons = [
  [
    "One Platform for Your Travel Needs",
    "Explore multiple travel services without having to manage every requirement separately.",
  ],
  [
    "Customized Travel Solutions",
    "Travel plans can be arranged according to your destination, dates, number of travellers and preferences.",
  ],
  [
    "Domestic & International Options",
    "Plan trips within India as well as international holidays.",
  ],
  [
    "Customer Assistance",
    "Our travel team is available to assist customers with enquiries, bookings and travel-related requirements.",
  ],
  [
    "Convenient Booking Experience",
    "We aim to simplify the travel planning and booking process through an easy-to-use online platform.",
  ],
];

const approachSteps = [
  {
    number: "01",
    title: "Discover",
    text: "Explore destinations, flights, hotels and holiday options.",
  },
  {
    number: "02",
    title: "Plan",
    text: "Choose travel services according to your dates, preferences and budget.",
  },
  {
    number: "03",
    title: "Book",
    text: "Complete your travel arrangements through our platform and booking assistance.",
  },
  {
    number: "04",
    title: "Travel",
    text: "Receive relevant booking information and customer support for your journey.",
  },
];

const commitments = [
  "Convenient travel booking solutions",
  "Domestic and international travel options",
  "Clear booking information",
  "Secure payment processing",
  "Customer-focused assistance",
  "Customized travel solutions",
  "Coordination with trusted travel service providers",
  "Support throughout the booking process",
];

const destinations = [
  "Kashmir",
  "Himachal Pradesh",
  "Kerala",
  "Goa",
  "Rajasthan",
  "Thailand",
  "Vietnam",
  "Dubai",
  "Bali",
  "Singapore",
  "Malaysia",
];

export default function About() {
  usePageMeta(
  "About MakeBeeTrip | B2C Travel Company in New Delhi",
  "MakeBeeTrip Private Limited is a New Delhi-based B2C travel company for flights, hotels, buses, cabs, holiday packages and visa assistance in India and abroad.",
  "about makebeetrip, makebeetrip private limited, b2c travel company, travel agency new delhi, flight booking, hotel booking, cab booking, holiday packages, visa assistance, domestic and international travel"
);
  return (
    <main className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="about-copy">
          <span className="about-eyebrow">About Us</span>

          <h1>
            We Make Trips,
            <br />
            You Make <em>Memories.</em>
          </h1>

          <p>
            Welcome to MakeBeeTrip Private Limited, a B2C online travel company
            based in New Delhi, India, offering convenient travel booking and
            holiday planning solutions for customers across India and
            international destinations.
          </p>

          <div className="about-promises">
            {promises.map((item) => (
              <article className="about-promise" key={item.title}>
                <span>{item.icon}</span>

                <div>
                  <strong>{item.title}</strong>
                  <small>{item.text}</small>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="about-visual">
          <img
            src={aboutImage}
            alt="Traveler enjoying a mountain journey"
          />
        </div>

        <img
          className="about-sticker"
          src={aboutIcon}
          alt=""
          aria-hidden="true"
        />
      </section>

      {/* Mission */}
      <section className="about-mission">
        <Plane className="about-paper" size={58} />

        <div>
          <span>Our Mission</span>

          <p>
            Our goal is simple — to make travel planning easier, more
            convenient and accessible for everyone. From your first travel
            enquiry to your booking and trip arrangements, MakeBeeTrip brings
            multiple travel services together on one platform.
          </p>
        </div>

        <Luggage className="about-suitcase" size={64} />
      </section>

      {/* Who We Are */}
      <section className="about-info-section">
        <div className="about-info-content">
          <span className="about-section-kicker">Who We Are</span>

          <h2>Travel solutions built around your journey</h2>

          <p>
            MakeBeeTrip is built to serve individual travellers, couples,
            families, groups and corporate travellers looking for reliable
            travel solutions.
          </p>

          <p>
            Whether you are planning a weekend getaway, family vacation,
            honeymoon, international holiday, business trip or group tour, our
            platform allows you to explore and arrange the travel services you
            need.
          </p>

          <p>
            We work with airlines, hotels, transportation providers,
            destination partners, visa service providers and other travel
            suppliers to facilitate travel arrangements for our customers.
          </p>
        </div>

        <div className="about-info-highlight">
          <Handshake size={32} />

          <strong>Your Journey, Our Commitment.</strong>

          <span>
            Bringing travel services together to make planning simpler.
          </span>
        </div>
      </section>

      {/* Travel Services */}
      <section className="about-grid-section">
        <div className="about-panel about-services-panel">
          <span className="about-section-kicker">Our Travel Services</span>

          <h2>Everything you need for your journey</h2>

          <div className="about-offers">
            {offers.map((offer) => (
              <article className="about-offer" key={offer.label}>
                <span>{offer.icon}</span>

                <div>
                  <strong>{offer.label}</strong>
                  <p>{offer.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="about-panel">
          <span className="about-section-kicker">
            Why Choose MakeBeeTrip?
          </span>

          <h2>Travel planning made simpler</h2>

          <div className="about-reasons">
            {reasons.map(([title, text]) => (
              <article key={title}>
                <HeartHandshake size={17} />

                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Domestic & International */}
      <section className="about-destinations">
        <div className="about-destinations-header">
          <span className="about-section-kicker">
            Domestic & International Travel
          </span>

          <h2>From destinations across India to international holidays</h2>

          <p>
            MakeBeeTrip offers travel solutions for both domestic and
            international holidays. From destinations across India to popular
            international destinations, our team helps customers find travel
            options according to their requirements.
          </p>
        </div>

        <div className="about-destination-list">
          {destinations.map((destination) => (
            <span key={destination}>
              <MapPin size={14} />
              {destination}
            </span>
          ))}
        </div>
      </section>

      {/* Our Approach */}
      <section className="about-approach">
        <div className="about-approach-header">
          <span className="about-section-kicker">Our Approach</span>

          <h2>Simple steps from planning to travel</h2>

          <p>
            We believe that booking a trip should be simple and transparent.
            Our approach is based on four simple steps.
          </p>
        </div>

        <div className="about-approach-grid">
          {approachSteps.map((step) => (
            <article className="about-approach-card" key={step.number}>
              <span className="about-step-number">{step.number}</span>

              <h3>{step.title}</h3>

              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Commitment */}
      <section className="about-commitment">
        <div>
          <span className="about-section-kicker">Our Commitment</span>

          <h2>Focused on a convenient travel experience</h2>

          <p>
            At MakeBeeTrip, we are committed to providing convenient travel
            solutions while supporting customers throughout the booking
            process.
          </p>
        </div>

        <div className="about-commitment-list">
          {commitments.map((commitment) => (
            <div key={commitment}>
              <CheckCircle2 size={17} />
              <span>{commitment}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Vision */}
      <section className="about-vision">
        <span>Our Vision</span>

        <h2>Making Travel Planning Easier</h2>

        <p>
          Our vision is to build a convenient and customer-focused travel
          platform that connects travellers with the services they need to
          plan and book their journeys.
        </p>

        <p>
          We aim to make travel planning easier by bringing{" "}
          <strong>
            Flights, Hotels, Holidays, Cabs, Bus and Visa Assistance
          </strong>{" "}
          together under one platform.
        </p>
      </section>

      {/* Company Information */}
      <section className="about-company">
        <div className="about-company-card">
          <span className="about-section-kicker">
            Company Information
          </span>

          <h2>MakeBeeTrip Private Limited</h2>

          <div className="about-company-details">
            <div>
              <small>Legal Name</small>
              <strong>MAKEBEETRIP PRIVATE LIMITED</strong>
            </div>

            <div>
              <small>Corporate Identity Number (CIN)</small>
              <strong>U79110DC2026PTC471178</strong>
            </div>

            <div>
              <small>Registered / Mailing Address</small>
              <strong>
                WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi –
                110018, Delhi, India
              </strong>
            </div>
          </div>
        </div>

        <div className="about-company-card about-contact-card">
          <span className="about-section-kicker">Contact Us</span>

          <h2>MakeBeeTrip Private Limited</h2>

          <div className="about-contact-details">
            <a href="tel:9355471999">
              <Headphones size={18} />
              <span>9355471999</span>
            </a>

            <a href="mailto:Holidays@makebeetrip.com">
              <BadgeCheck size={18} />
              <span>Holidays@makebeetrip.com</span>
            </a>

            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noreferrer"
            >
              <Globe2 size={18} />
              <span>makebeetrip.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="about-disclaimer">
        <ShieldCheck size={18} />

        <p>
          Travel services are subject to availability and the applicable
          terms, conditions, cancellation policies and fare rules of the
          respective airline, hotel, transportation provider, visa service
          provider or other travel supplier.
        </p>
      </section>
    </main>
  );
}
